"use client";

import { useEffect, useState } from "react";
import ViatorProductInfo from "./ViatorProductInfo";
import { readViatorActivityContent } from "@/lib/viator-product-info";

/**
 * Laadt de Viator-inhoud van een activiteit IN DE BROWSER, na het laden van
 * de pagina (certificering F4b, 23-09-2026).
 *
 * WAAROM CLIENT-SIDE: Viators richtlijn "Protecting unique content" vraagt
 * dat hun tekst nooit in de broncode van de geladen pagina staat, en ons
 * certificeringsformulier zegt "unique Viator content is not indexed".
 * Tot deze wijziging rendeerde ViatorProductInfo server-side in page.js,
 * dus stond de tekst in de HTML die zoekmachines lezen. Nu:
 *  - de server rendert dit component als NIETS (de state begint leeg en
 *    `useEffect` draait niet op de server);
 *  - de browser haalt /api/viator-content op; dat pad valt onder
 *    `Disallow: /api/` in robots.txt (src/app/robots.js) en de route zet
 *    zelf `X-Robots-Tag: noindex, nofollow`.
 *
 * FAALT STIL: gaat het ophalen mis, of is er niets, dan blijft dit component
 * leeg. Geen foutmelding voor de klant, de rest van de pagina werkt gewoon.
 *
 * Een fetch per deal+taal, gedeeld tussen de activiteiten van dezelfde
 * pagina (de meeste deals hebben er een, sommige twee of drie).
 */

const inFlight = new Map();

export function viatorContentUrl(dealId, locale) {
  const params = new URLSearchParams({
    dealId: String(dealId),
    locale: String(locale || "nl"),
  });
  return `/api/viator-content?${params.toString()}`;
}

export function fetchViatorContent(dealId, locale) {
  const url = viatorContentUrl(dealId, locale);
  if (!inFlight.has(url)) {
    const request = fetch(url, { credentials: "omit" })
      .then((res) => (res.ok ? res.json() : null))
      .catch(() => null);
    inFlight.set(url, request);
    // Alleen een antwoord MET activiteiten vasthouden. Een mislukte poging
    // of een leeg antwoord ("nog niets", bijvoorbeeld vlak na een deploy en
    // voor de audit) is tijdelijk; de route geeft dat daarom `no-store`, en
    // dus onthouden we het hier ook niet (Codex, ronde 5).
    request.then((payload) => {
      const activities = payload?.data?.activities ?? payload?.activities;
      if (!Array.isArray(activities) || activities.length === 0) {
        inFlight.delete(url);
      }
    });
  }
  return inFlight.get(url);
}

/** Alleen voor proeven: de gedeelde fetches vergeten. */
export function __resetViatorContentCache() {
  inFlight.clear();
}

export default function ViatorProductContent({ dealId, activityId, locale = "nl" }) {
  // De opgehaalde blokken worden bewaard SAMEN met de sleutel (deal,
  // activiteit, taal) waar ze bij horen, en alleen getoond als die sleutel
  // nog de huidige is (Codex, ronde 5). page.js rendert de activiteiten met
  // key={i}; een client-side navigatie naar een andere deal kan dus dezelfde
  // componentpositie hergebruiken. Zonder deze controle bleef de inhoud van
  // de VORIGE activiteit (aanbiederstekst, verzamelpunt, annulering) staan tot
  // de nieuwe fetch klaar was, en bij een ongeldig id zelfs voorgoed.
  const sleutel = `${dealId}|${activityId}|${locale}`;
  const [opgehaald, setOpgehaald] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const id = Number(dealId);
    if (!Number.isInteger(id) || id <= 0) return undefined;
    fetchViatorContent(id, locale).then((payload) => {
      if (cancelled) return;
      setOpgehaald({
        sleutel,
        blocks: readViatorActivityContent(payload, activityId),
      });
    });
    return () => {
      cancelled = true;
    };
  }, [dealId, activityId, locale, sleutel]);

  const blocks =
    opgehaald && opgehaald.sleutel === sleutel ? opgehaald.blocks : null;
  if (!blocks) return null;
  return (
    <ViatorProductInfo
      productInfo={blocks.productInfo}
      content={blocks.content}
      locale={locale}
    />
  );
}
